'use client';

interface BillingCycleToggleProps {
    isYearly: boolean;
    onChange: (isYearly: boolean) => void;
    annualDiscountPct?: number;
    isDarkMode?: boolean;
}

/**
 * Pill-style toggle for switching between monthly and annual billing cycles.
 * Shows "X% off" badge on the Annual option to encourage yearly subscriptions.
 */
export function BillingCycleToggle({
    isYearly,
    onChange,
    annualDiscountPct = 50,
    isDarkMode = true,
}: BillingCycleToggleProps) {
    return (
        <div
            className={`flex p-1 rounded-lg ${
                isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
            }`}
        >
            {/* Monthly Option */}
            <button
                type="button"
                onClick={() => onChange(false)}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                    !isYearly
                        ? isDarkMode
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'bg-white text-gray-900 shadow-sm'
                        : isDarkMode
                        ? 'text-gray-400 hover:text-gray-300'
                        : 'text-gray-500 hover:text-gray-700'
                }`}
            >
                Monthly
            </button>

            {/* Annual Option with Best Value label and Discount Badge */}
            <button
                type="button"
                onClick={() => onChange(true)}
                className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 flex flex-col items-center justify-center ${
                    isYearly
                        ? isDarkMode
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'bg-white text-gray-900 shadow-sm'
                        : isDarkMode
                        ? 'text-gray-400 hover:text-gray-300'
                        : 'text-gray-500 hover:text-gray-700'
                }`}
            >
                {/* Best Value - small label on top */}
                <span
                    className={`text-[10px] font-semibold uppercase tracking-wide ${
                        isYearly
                            ? isDarkMode
                                ? 'text-amber-300'
                                : 'text-amber-600'
                            : isDarkMode
                            ? 'text-amber-500/70'
                            : 'text-amber-600/70'
                    }`}
                >
                    Best Value!
                </span>
                {/* Annual + 50% off on same line */}
                <div className="flex items-center gap-1.5">
                    <span>Annual</span>
                    <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                            isYearly
                                ? isDarkMode
                                    ? 'bg-green-500 text-white'
                                    : 'bg-green-500 text-white'
                                : isDarkMode
                                ? 'bg-green-600/30 text-green-400'
                                : 'bg-green-100 text-green-700'
                        }`}
                    >
                        {annualDiscountPct}% off
                    </span>
                </div>
            </button>
        </div>
    );
}

export default BillingCycleToggle;
